export type TechnicalContentTheme = 'light' | 'dark'
type TechnicalContentMediaType = 'image' | 'video'
type TechnicalContentVideoProvider = 'youtube' | 'pixl'

export interface TechnicalContentMainMedia {
  mediaType: TechnicalContentMediaType
  image?: string
  imageAlt?: string
  videoProvider?: TechnicalContentVideoProvider
  videoUrl?: string
  videoTitle?: string
  videoPoster?: string
}

export interface TechnicalContentGalleryImage {
  image: string
  imageAlt: string
}

export interface TechnicalContentProduct {
  productName: string
  ctaText: string
  ctaLink: string
}

export interface TechnicalContentItem {
  mainMedia: TechnicalContentMainMedia
  title: string
  description: string
}

export interface TechnicalContentProps {
  enableSession?: boolean
  theme?: TechnicalContentTheme
  items?: TechnicalContentItem[]
  imageGallery?: TechnicalContentGalleryImage[]
  product?: TechnicalContentProduct
}

const isNonEmptyString = (value?: string) => {
  return Boolean(value?.trim())
}

const isValidGalleryImage = (image?: Partial<TechnicalContentGalleryImage>) => {
  return isNonEmptyString(image?.image) && isNonEmptyString(image?.imageAlt)
}

const isValidMainMedia = (media?: TechnicalContentMainMedia) => {
  if (!media) {
    return false
  }

  if (media.mediaType === 'image') {
    return isNonEmptyString(media.image) && isNonEmptyString(media.imageAlt)
  }

  if (media.mediaType === 'video') {
    return (
      (media.videoProvider === 'youtube' || media.videoProvider === 'pixl') &&
      isNonEmptyString(media.videoUrl) &&
      isNonEmptyString(media.videoTitle)
    )
  }

  return false
}

const isValidProduct = (product?: Partial<TechnicalContentProduct>) => {
  return (
    isNonEmptyString(product?.productName) &&
    isNonEmptyString(product?.ctaText) &&
    isNonEmptyString(product?.ctaLink)
  )
}

const isValidTechnicalContentItem = (
  item?: Partial<TechnicalContentItem>
): item is TechnicalContentItem => {
  if (!item) {
    return false
  }

  return (
    isValidMainMedia(item.mainMedia) &&
    isNonEmptyString(item.title) &&
    isNonEmptyString(item.description)
  )
}

export const normalizeTechnicalContentItems = (
  items?: TechnicalContentItem[]
) => {
  if (!Array.isArray(items)) {
    return []
  }

  return items.filter(isValidTechnicalContentItem)
}

export const normalizeTechnicalContentGallery = (
  imageGallery?: TechnicalContentGalleryImage[]
) => {
  if (!Array.isArray(imageGallery)) {
    return []
  }

  return imageGallery.filter(isValidGalleryImage)
}

export const normalizeTechnicalContentProduct = (
  product?: TechnicalContentProduct
) => {
  return isValidProduct(product) ? product : null
}
